import { useCallback } from 'react';
import { useAbility } from '@casl/react';
import { Button, firstDefined } from '@gafe/common-ui';
import { filter } from 'lodash';

import { WarningPopOver } from '@/components/generic/WarningPopOver';
import {
  markStepAsCompleted,
  useOrderContext,
  useOrderUpdate,
} from '@/components/modals/SubmitOrder/helpers';
import { EnergyStorage } from '@/components/remote/EnergyStorage/EnergyStorage';
import { CalculationMethod } from '@/controllers/Activity';
import { useBomTotal } from '@/hooks';
import { AbilityContext } from '@/hooks/ability/Can';
import { ProposalType } from '@/types';

import { getOrderTotal, isEquipment, isInventory, OLD_SERVICES_LIST_VIEW } from '../../constants';
import { getStepByName } from '../../helpers';
import { StepsName } from '../../types';
import { InventoryType } from '../Common/InventoryType';
import { ProposalDetails } from '../Common/ProposalDetails';

import AdderDetails from './components/AdderDetails';
import { AddonsDetails } from './components/AddonsDetails';
import { EquipmentDetails } from './components/EquipmentDetails';
import { JobDetails } from './components/JobDetails';
import { OrderTotal } from './components/OrderTotal';
import ServiceDetails from './components/ServiceDetails';
import { Warranty } from './components/Warranty';
import { canHaveErrors, getProductErrors } from './constants';
import { Props } from './types';

import styles from './OrderDetailsStep.module.scss';

export const OrderDetailsStep = ({
  proposal,
  productLineName,
  opportunity,
  allPriceBookEntries,
  goToStep,
  close,
  stepName,
}: Props) => {
  const state = useOrderContext();
  const updateOrder = useOrderUpdate();
  const ability = useAbility(AbilityContext);
  const shippingStep = getStepByName(state, StepsName['Shipping']);
  const bomTotal = useBomTotal(state, proposal);
  const isBulkFulfillmentEnabled =
    ability.can('view', 'Bulk Fulfillment Program') &&
    proposal.proposalType === ProposalType['Timberline Solar'];

  // bulk orders override the SKU pricing
  const enableSkuStep = stepName === 'Order Details' || stepName === 'Products';
  const enableSkuFlow =
    ability.can('view', 'SKU_PRICING') &&
    enableSkuStep &&
    proposal.priceCalculationMethod === CalculationMethod.SKU &&
    !isBulkFulfillmentEnabled;

  const confirmationStepName = () => {
    if (state.isAccessProgram && state.isDistribution) {
      return 'Proceed to review contract';
    }

    if (!state.isAccessProgram && state.isDistribution) {
      return 'review order';
    }

    return `Proceed to ${StepsName['Shipping']}`;
  };

  const confirmationStep = getStepByName(
    state,
    state.isAccessProgram ? StepsName['Request Contract'] : StepsName['Confirmation'],
  );
  const isActivityDistributionModel = state.isDistribution;
  const isAccessModel = state.isAccessProgram;
  const nextStep = isActivityDistributionModel ? confirmationStep : shippingStep;

  const goToNextStep = useCallback(() => {
    goToStep?.(nextStep);
    markStepAsCompleted(state.wizardHeaderSteps, stepName);
  }, [goToStep, nextStep, state.wizardHeaderSteps, stepName]);

  const selectedInventory = filter(
    state.visibleOrderItems,
    oi => isEquipment(oi) && isInventory(oi),
  );

  const isPartialReRoof = ((): boolean | null => {
    if (state.order.partialReRoof !== null) {
      return state.order.partialReRoof;
    }

    if (!opportunity?.partialReRoof) {
      return null;
    }

    return Boolean(opportunity?.partialReRoof);
  })();

  const isPartialReRoofDeselected = isPartialReRoof === null;

  const isInventoryAvailable = selectedInventory.length > 0;
  const shouldShowInventorySection = isBulkFulfillmentEnabled && isInventoryAvailable;
  const validateInventorySelected = (): boolean => {
    if (isBulkFulfillmentEnabled && isInventoryAvailable) {
      return state.order.inventoryType !== null;
    }

    return true;
  };

  const productErrors = isActivityDistributionModel
    ? []
    : getProductErrors({
        state,
        allPriceBookEntries,
        isPartialReRoofDeselected:
          state.order.inventoryType === null && shouldShowInventorySection
            ? false
            : isPartialReRoofDeselected,
        productLineName,
        inventorySelected: validateInventorySelected(),
      });

  // we need to disable 'proceed' button when asphalt
  // shingle quantity is selected, but color is not,
  // or there are some product errors
  const isNextStepDisabled =
    canHaveErrors(productLineName, isPartialReRoofDeselected) && productErrors.length > 0;

  const prodWrapper = (
    <>
      <InventoryType />
      {state.order.inventoryType === null && shouldShowInventorySection ? null : (
        <>
          {!OLD_SERVICES_LIST_VIEW && <ServiceDetails />}
          <EquipmentDetails proposalBom={enableSkuFlow ? proposal : null} />
          <br />
          <AddonsDetails />
          <EnergyStorage
            onOrderChange={updateOrder}
            opportunity={firstDefined(undefined, opportunity)}
            order={state}
          />
          <br />
          {OLD_SERVICES_LIST_VIEW && <ServiceDetails />}
          <Warranty />
          {!state.isPriceHidden && (
            <OrderTotal
              title={enableSkuFlow ? 'Total Costs' : 'Total'}
              total={enableSkuFlow ? bomTotal : getOrderTotal(state, productLineName, proposal)}
            />
          )}
          <JobDetails isPartialReRoof={isPartialReRoof} />
          <br />
        </>
      )}
    </>
  );

  const serviceWrapper = (
    <>
      {isAccessModel && (
        <>
          <EnergyStorage
            onOrderChange={updateOrder}
            opportunity={firstDefined(undefined, opportunity)}
            order={state}
          />
          <br />
        </>
      )}
      <ServiceDetails />
      <AdderDetails />
    </>
  );

  return (
    <div data-testid="gafe-order-details-step">
      <ProposalDetails orderItems={state.visibleOrderItems} shouldDisplayPurchaseNumber={false} />
      <br />
      {isActivityDistributionModel ? serviceWrapper : prodWrapper}
      <div className={styles.footer_wrapper}>
        <Button colorSchema="neutral" onClick={close} size="large" variant="text">
          Cancel
        </Button>
        <WarningPopOver productErrors={productErrors}>
          <Button
            colorSchema="primary"
            disabled={isNextStepDisabled}
            onClick={goToNextStep}
            size="large"
          >
            <span>{confirmationStepName()}</span>
          </Button>
        </WarningPopOver>
      </div>
    </div>
  );
};
